import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useAppContext } from 'context/AppContext';
import { required } from 'form/validation';
import { fetchStorageGroupBookingCalendar } from 'actions/storageGroups';
import { handleResponse } from 'actions/actionHelpers';
import { enrichStorages } from 'logic/bookingLogic';
import { add, sub, startOfDay } from 'date-fns';
import { getLongDateFormat, formatIsoDate, parseDate } from 'helpers/DateHelper';
import { makeStyles } from 'styles/util';
import bookingStatuses from 'enums/bookingStatuses';
import { getStorageGroupTitle, formatStorageListItem } from 'helpers/StorageSiteHelper';

import Form from 'form/Form';
import Button from '@material-ui/core/Button';
import { TextField, showErrorOnBlur } from 'mui-rff';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import DatePicker from 'form/DatePicker';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import { OnChange } from 'react-final-form-listeners';
import LoadingError from 'common/LoadingError';

const useStyles = makeStyles(({ colors }) => ({
    notAvailable: {
        color: colors.textColorDisabled
    }
}));

const ChangeStorageDialog = ({ booking, storageSite, open, onSubmit, onCancel }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { appContext } = useAppContext();
    const today = startOfDay(new Date());
    let firstAvailableStartDate = parseDate(booking.startDate, appContext);
    if(today > firstAvailableStartDate) {
        firstAvailableStartDate = today;
    }

    const [bookingCalendar, setBookingCalendar] = useState(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingError, setLoadingError] = useState(undefined);

    useEffect(() => {
        if(open) {
            loadStorageGroupBookingCalendar(booking.storageGroupId, firstAvailableStartDate);
        }
    }, [open]);

    const loadStorageGroupBookingCalendar = storageGroupId => {
        const storageGroup = storageSite.storageGroups.find(o => o.id === storageGroupId);
        if(!storageGroup.enumerateStorages) {
            return;
        }
        setIsLoading(true);
        dispatch(fetchStorageGroupBookingCalendar(storageGroupId, formatIsoDate(firstAvailableStartDate, appContext), booking.id, true /* includeHiddenStorages */))
            .then(handleResponse(
                response => {
                    setBookingCalendar(response.payload);
                    setIsLoading(false);
                },
                () => {
                    setLoadingError(true);
                    setIsLoading(false);
                }
            ));
    };

    const handleStorageGroupChange = (form, values) => {
        loadStorageGroupBookingCalendar(values.storageGroupId);
        form.change('storageId', undefined);
    };

    const handleFormSubmit = values => {
        onSubmit(values);
    };

    if(!booking || !storageSite) {
        return null;
    }

    const showStartDateField = booking.bookingStatus === bookingStatuses.purchased.key;

    const initialValues = {
        storageGroupId: booking.storageGroupId,
        storageId: booking.storageId,
        startDate: showStartDateField
            ? firstAvailableStartDate
            : undefined
    };

    return (
        <Dialog fullWidth maxWidth="md" open={open} onClose={onCancel}>
            <Form
                initialValues={initialValues}
                onSubmit={handleFormSubmit}
            >
                {({ form, values, invalid, handleSubmit }) => {
                    const storageGroup = storageSite.storageGroups.find(o => o.id === values.storageGroupId);
                    const storages = !isLoading && storageGroup.enumerateStorages && bookingCalendar
                        ? enrichStorages(storageGroup, bookingCalendar, values.startDate, booking.endDate, booking.storageId)
                        : undefined;
                    return (
                        <form onSubmit={handleSubmit}>
                            <DialogTitle disableTypography>
                                <Typography variant="h5">
                                    Change storage
                                </Typography>
                            </DialogTitle>
                            <DialogContent>
                                {
                                    showStartDateField &&
                                    (
                                        <DatePicker
                                            name="startDate"
                                            label="Start date"
                                            formatFunc={getLongDateFormat}
                                            inputVariant="outlined"
                                            showError={showErrorOnBlur}
                                            required
                                            fieldProps={{
                                                validate: required,
                                                minDate: formatIsoDate(firstAvailableStartDate, appContext),
                                                maxDate: formatIsoDate(sub(add(today, { months: 1 }), { days: 1 }), appContext) }}
                                            autoFocus
                                        />
                                    )
                                }

                                <TextField
                                    select
                                    name="storageGroupId"
                                    label="Size"
                                    variant="outlined"
                                    required
                                    fieldProps={{ validate: required }}
                                >
                                    {
                                        storageSite.storageGroups.map(sg => (
                                            <MenuItem key={sg.id} value={sg.id}>
                                                {getStorageGroupTitle(sg, appContext, { useSpecialCustomAreaLabel: true })}
                                            </MenuItem>
                                        ))
                                    }
                                </TextField>
                                <OnChange name="storageGroupId">
                                    {() => handleStorageGroupChange(form, values)}
                                </OnChange>

                                {
                                    storages &&
                                    (
                                        <TextField
                                            select
                                            name="storageId"
                                            label="Storage"
                                            variant="outlined"
                                            required
                                            fieldProps={{ validate: required }}
                                        >
                                            {
                                                storages.map(storage => (
                                                    <MenuItem key={storage.id} value={storage.id}>
                                                        {formatStorageListItem(storage, classes.notAvailable)}
                                                    </MenuItem>
                                                ))
                                            }
                                        </TextField>
                                    )
                                }
                                {
                                    isLoading && storageGroup.enumerateStorages &&
                                    (
                                        <CircularProgress
                                            size={16}
                                            color="primary"
                                        />
                                    )

                                }
                                {
                                    loadingError && <LoadingError />
                                }
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={invalid}
                                >
                                    Save
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={onCancel}
                                >
                                    Cancel
                                </Button>
                            </DialogActions>
                        </form>
                    );
                }}
            </Form>
        </Dialog>
    );
};

ChangeStorageDialog.propTypes = {
    booking: PropTypes.object.isRequired,
    storageSite: PropTypes.object.isRequired,
    open: PropTypes.bool.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default ChangeStorageDialog;
